import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Calendar, Clock, MapPin, Users, DollarSign, Heart, MessageCircle, 
  Send, ArrowLeft, CheckCircle, Image as ImageIcon, Trash2, Share2, Facebook, Instagram, Link as LinkIcon, X
} from "lucide-react";

const EventDetail = () => {
  const { eventId } = useParams();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [rsvpStatus, setRsvpStatus] = useState({ has_rsvp: false, checked_in: false });
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [postingLoading, setPostingLoading] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);

  const fetchEventData = async () => {
    try {
      const [eventRes, postsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/posts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
      ]);
      setEvent(eventRes.data);
      setPosts(postsRes.data);

      if (token) {
        try {
          const rsvpRes = await axios.get(`${API}/events/${eventId}/rsvp/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setRsvpStatus(rsvpRes.data);
        } catch (e) {}
      }
    } catch (error) {
      console.error("Failed to fetch event:", error);
      toast.error("Failed to load event details");
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async () => {
    if (!user) {
      toast.error("Please sign in to RSVP");
      return;
    }
    if (!user.is_subscriber) {
      toast.error("Subscription required to RSVP");
      return;
    }

    try {
      if (rsvpStatus.has_rsvp) {
        await axios.delete(`${API}/events/${eventId}/rsvp`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRsvpStatus({ has_rsvp: false, checked_in: false });
        toast.success("RSVP cancelled");
      } else {
        await axios.post(`${API}/events/${eventId}/rsvp`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRsvpStatus({ has_rsvp: true, checked_in: false });
        toast.success("RSVP confirmed!");
      }
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update RSVP");
    }
  };

  const handleCheckin = async () => {
    if (!rsvpStatus.has_rsvp) {
      toast.error("Please RSVP first");
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/checkin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRsvpStatus(prev => ({ ...prev, checked_in: true }));
      toast.success("Checked in! Enjoy the show!");
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in");
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, GIF, WebP");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size: 5MB");
      return;
    }

    setSelectedImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return null;

    const formData = new FormData();
    formData.append('file', selectedImage);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.image_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      return null;
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() && !selectedImage) return;
    if (!user?.is_subscriber) {
      toast.error("Subscription required to post");
      return;
    }

    setPostingLoading(true);
    try {
      let imageUrl = null;
      
      // Upload image first if selected
      if (selectedImage) {
        setUploadingImage(true);
        imageUrl = await uploadImage();
        setUploadingImage(false);
        
        if (!imageUrl && !newPost.trim()) {
          setPostingLoading(false);
          return;
        }
      }

      const response = await axios.post(`${API}/posts`, {
        event_id: eventId,
        content: newPost.trim(),
        image_url: imageUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => [response.data, ...prev]);
      setNewPost("");
      clearImage();
      toast.success("Post created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setPostingLoading(false);
      setUploadingImage(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!user?.is_subscriber) {
      toast.error("Subscription required to like posts");
      return;
    }

    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, likes: p.likes + (response.data.liked ? 1 : -1), liked_by_user: response.data.liked }
          : p
      ));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-20 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-obsidian pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 text-lg">Event not found</p>
          <Link to="/events" className="text-electric hover:underline mt-4 inline-block">
            ← Back to Events
          </Link>
        </div>
      </div>
    );
  }

  // Check if date is recurring (e.g., "Every Monday") or a specific date
  const isRecurring = event.date?.startsWith("Every");
  const eventDate = isRecurring ? null : new Date(event.date);
  const displayDate = isRecurring 
    ? event.date 
    : eventDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-obsidian pt-20" data-testid="event-detail-page">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-96">
          <img 
            src={event.image_url || "/assets/hero-sax.jpg"}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/50 via-obsidian/80 to-obsidian"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <Link to="/events" className="inline-flex items-center gap-2 text-white/50 hover:text-electric mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </Link>

          <div className="grid lg:grid-cols-[1fr_400px] gap-8">
            {/* Event Info */}
            <div>
              {event.is_featured && (
                <span className="inline-block bg-electric text-obsidian text-xs font-medium px-3 py-1 rounded mb-4">
                  Featured Event
                </span>
              )}
              <h1 className="font-display text-4xl sm:text-5xl text-white mb-4" data-testid="event-title">
                {event.title}
              </h1>
              <p className="text-electric font-display text-xl italic mb-6">{event.performer}</p>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-white/70">
                  <Calendar className="w-5 h-5 text-electric" />
                  <span>{displayDate}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Clock className="w-5 h-5 text-electric" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <MapPin className="w-5 h-5 text-electric" />
                  <span>2756 Hennepin Ave, Minneapolis</span>
                </div>
              </div>

              <p className="text-white/70 leading-relaxed mb-8">{event.description}</p>

              {event.performer_bio && (
                <div className="border border-white/10 rounded-lg p-6 bg-obsidian-light mb-8">
                  <h3 className="font-display text-lg text-white mb-3">About the Artist</h3>
                  <p className="text-white/60">{event.performer_bio}</p>
                </div>
              )}
            </div>

            {/* Ticket Card */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="bg-obsidian-light border border-white/10 rounded-lg p-6" data-testid="ticket-card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white/50 text-sm">Ticket Price</p>
                    <p className="text-3xl font-display text-electric">${event.ticket_price.toFixed(2)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {user?.is_subscriber ? (
                    <>
                      <Button 
                        onClick={handleRSVP}
                        className={`w-full py-6 ${rsvpStatus.has_rsvp 
                          ? "bg-red-900 hover:bg-red-900/80" 
                          : "bg-electric text-obsidian hover:bg-electric-light"}`}
                        data-testid="rsvp-btn"
                      >
                        {rsvpStatus.has_rsvp ? "Cancel RSVP" : "RSVP Now"}
                      </Button>
                      {rsvpStatus.has_rsvp && !rsvpStatus.checked_in && (
                        <Button 
                          onClick={handleCheckin}
                          variant="outline"
                          className="w-full py-6 border-electric text-electric hover:bg-electric/10"
                          data-testid="checkin-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Check In
                        </Button>
                      )}
                      {rsvpStatus.checked_in && (
                        <div className="flex items-center justify-center gap-2 text-green-500 py-3">
                          <CheckCircle className="w-5 h-5" />
                          <span>Checked In!</span>
                        </div>
                      )}
                    </>
                  ) : user ? (
                    <Link to="/membership" className="block">
                      <Button className="w-full py-6 bg-electric text-obsidian hover:bg-electric-light">
                        Subscribe to RSVP
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/login" className="block">
                      <Button className="w-full py-6 bg-electric text-obsidian hover:bg-electric-light">
                        Sign In to RSVP
                      </Button>
                    </Link>
                  )}
                </div>

                {/* Share Event */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/50 text-sm mb-3">Share this event</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const url = window.location.href;
                        const text = `Check out ${event.title} at Brown𝄞tone Jazz Club!`;
                        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                      }}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                      data-testid="share-facebook"
                    >
                      <Facebook className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        window.open('https://www.instagram.com/brownstonejazzmn/', '_blank');
                      }}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                      data-testid="share-instagram"
                    >
                      <Instagram className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success("Link copied to clipboard!");
                      }}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                      data-testid="share-copy-link"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Feed */}
      <section className="py-12 border-t border-white/10" data-testid="community-feed">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl text-white mb-8">
            Community Feed
            <span className="text-white/40 text-base ml-2 font-body">({posts.length} posts)</span>
          </h2>

          {/* New Post Form */}
          {user?.is_subscriber ? (
            <div className="bg-obsidian-light border border-white/10 rounded-lg p-4 mb-8" data-testid="new-post-form">
              <Textarea 
                placeholder="Share your thoughts about this event..."
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                className="bg-obsidian border-white/20 text-white placeholder:text-white/40 mb-4 resize-none"
                rows={3}
              />
              
              {/* Image Preview */}
              {imagePreview && (
                <div className="relative mb-4 inline-block">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-h-48 rounded-lg object-cover"
                  />
                  <button
                    onClick={clearImage}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    data-testid="remove-image-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                {/* Image Upload Button */}
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-white/50 hover:text-electric hover:bg-white/5 transition-colors"
                    data-testid="upload-image-btn"
                  >
                    <ImageIcon className="w-5 h-5" />
                    <span className="text-sm">Add Photo</span>
                  </label>
                </div>
                
                <Button 
                  onClick={handleCreatePost}
                  disabled={(!newPost.trim() && !selectedImage) || postingLoading}
                  className="bg-electric text-obsidian hover:bg-electric-light"
                  data-testid="submit-post-btn"
                >
                  {postingLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
                      {uploadingImage ? "Uploading..." : "Posting..."}
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Post
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-obsidian-light border border-white/10 rounded-lg p-6 mb-8 text-center">
              <p className="text-white/50">
                {user 
                  ? <Link to="/membership" className="text-electric hover:underline">Subscribe</Link>
                  : <Link to="/login" className="text-electric hover:underline">Sign in</Link>
                } to join the conversation
              </p>
            </div>
          )}

          {/* Posts List */}
          <div className="space-y-6">
            {posts.length === 0 ? (
              <div className="text-center py-12 border border-white/10 rounded-lg bg-obsidian-light">
                <MessageCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No posts yet. Be the first to share!</p>
              </div>
            ) : (
              posts.map(post => (
                <PostCard 
                  key={post.id} 
                  post={post} 
                  currentUser={user}
                  onLike={() => handleLikePost(post.id)}
                  onDelete={() => handleDeletePost(post.id)}
                  expanded={expandedComments[post.id]}
                  onToggleComments={() => setExpandedComments(prev => ({
                    ...prev,
                    [post.id]: !prev[post.id]
                  }))}
                />
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const PostCard = ({ post, currentUser, onLike, onDelete, expanded, onToggleComments }) => {
  const { token } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    if (expanded) {
      fetchComments();
    }
  }, [expanded]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await axios.post(`${API}/comments`, {
        post_id: post.id,
        content: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => [...prev, response.data]);
      setNewComment("");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="feed-card" data-testid={`post-${post.id}`}>
      {/* Post Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-white font-medium">{post.user_name}</p>
          <p className="text-white/40 text-sm">{timeAgo(post.created_at)}</p>
        </div>
        {currentUser?.id === post.user_id && (
          <button 
            onClick={onDelete}
            className="text-white/30 hover:text-red-400 transition-colors p-1"
            data-testid={`delete-post-${post.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Post Content */}
      <p className="text-white/80 mb-4 whitespace-pre-wrap">{post.content}</p>

      {post.image_url && (
        <img 
          src={post.image_url.startsWith('/api/uploads') 
            ? `${process.env.REACT_APP_BACKEND_URL}${post.image_url}` 
            : post.image_url.startsWith('/uploads')
              ? `${process.env.REACT_APP_BACKEND_URL}/api${post.image_url}`
              : post.image_url
          } 
          alt="Post attachment"
          className="rounded-lg mb-4 max-h-96 w-full object-cover"
        />
      )}

      {/* Post Actions */}
      <div className="flex items-center gap-6 pt-3 border-t border-white/10">
        <button 
          onClick={onLike}
          className={`flex items-center gap-2 transition-colors ${
            post.liked_by_user ? "text-red-400" : "text-white/50 hover:text-red-400"
          }`}
          data-testid={`like-post-${post.id}`}
        >
          <Heart className={`w-5 h-5 ${post.liked_by_user ? "fill-current" : ""}`} />
          <span className="text-sm">{post.likes}</span>
        </button>
        <button 
          onClick={onToggleComments}
          className="flex items-center gap-2 text-white/50 hover:text-electric transition-colors"
          data-testid={`toggle-comments-${post.id}`}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm">Comments</span>
        </button>
      </div>

      {/* Comments Section */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-white/10">
          {loadingComments ? (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-electric border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {comments.map(comment => (
                <div key={comment.id} className="mb-3 pl-4 border-l-2 border-white/10">
                  <p className="text-white/80 text-sm">{comment.content}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {comment.user_name} • {timeAgo(comment.created_at)}
                  </p>
                </div>
              ))}

              {currentUser?.is_subscriber && (
                <div className="flex gap-2 mt-4">
                  <input 
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 bg-obsidian border border-white/20 rounded px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-electric focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button 
                    size="sm"
                    onClick={handleAddComment}
                    className="bg-electric text-obsidian hover:bg-electric-light"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventDetail;
